import { z } from 'zod';

import { ApiKeyScope } from '@nucleus-commerce/api-client/admin';

/** Same rule the backend applies to each allowed origin: scheme, host, optional port, no path. */
export const ORIGIN = /^https?:\/\/[a-z0-9.-]+(?::\d{1,5})?$/;

export const API_KEY_SCOPES = Object.values(ApiKeyScope);

/** One origin per line (commas also split); blank lines are ignored. */
export function parseOrigins(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((line) => line.trim().toLowerCase().replace(/\/+$/, ''))
    .filter(Boolean);
}

export const apiKeyFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, { error: 'apiKeys:validation.nameRequired' })
      .max(120, { error: 'apiKeys:validation.nameLength' }),
    kind: z.enum(['publishable', 'secret']),
    allowedOrigins: z.string(),
    scopes: z.array(z.enum(ApiKeyScope)),
    rateLimitPerMinute: z
      .string()
      .trim()
      .refine((value) => value === '' || /^\d+$/.test(value), {
        error: 'apiKeys:validation.rateLimit',
      })
      .refine((value) => value === '' || (Number(value) >= 10 && Number(value) <= 100_000), {
        error: 'apiKeys:validation.rateLimit',
      }),
  })
  .superRefine((values, ctx) => {
    if (values.kind !== 'publishable') return;
    const origins = parseOrigins(values.allowedOrigins);
    if (origins.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['allowedOrigins'],
        message: 'apiKeys:validation.originsRequired',
      });
    } else if (origins.length > 50) {
      ctx.addIssue({
        code: 'custom',
        path: ['allowedOrigins'],
        message: 'apiKeys:validation.originsMax',
      });
    } else if (!origins.every((origin) => ORIGIN.test(origin))) {
      ctx.addIssue({
        code: 'custom',
        path: ['allowedOrigins'],
        message: 'apiKeys:validation.origin',
      });
    }
  });

export type ApiKeyFormValues = z.infer<typeof apiKeyFormSchema>;

export function emptyApiKeyForm(): ApiKeyFormValues {
  return { name: '', kind: 'publishable', allowedOrigins: '', scopes: [], rateLimitPerMinute: '' };
}

/** Form fields a backend error may name; `allowedOrigins.2` and `scopes.0` land on the field as a whole. */
export const API_KEY_FIELDS: ReadonlySet<string> = new Set([
  'name',
  'kind',
  'allowedOrigins',
  'scopes',
  'rateLimitPerMinute',
]);

export const apiKeyFieldOf = (field: string) => field.replace(/\.\d+$/, '');
